"use client";

import React from "react";
import { motion } from "framer-motion";
import ScrambleText from "./ScrambleText";

const EASE = [0.22, 1, 0.36, 1] as const;
const REVEAL_EYEBROW = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [...EASE], delay: 0.1 },
} as const;
const REVEAL_WORD = {
  initial: { opacity: 0, y: "0.35em", filter: "blur(0.75rem)" },
  animate: { opacity: 1, y: "0em", filter: "blur(0rem)" },
} as const;
const WORD_TRANSITION = { duration: 0.8, ease: [...EASE] };
const WORD_DELAY = 0.25;
const WORD_STAGGER = 0.07;
const REVEAL_DESCRIPTION = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
} as const;

export default function ChangelogHero({
  eyebrow,
  title,
  description,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
}) {
  const words = title.split(" ");

  return (
    <header className="mx-auto flex w-full flex-col items-center gap-[2.5rem] px-[3rem] pt-[16.25rem] pb-[4rem] text-center lg:w-[113.125rem] lg:gap-[3rem] lg:px-0 lg:pt-[19.75rem] lg:pb-0">
      {eyebrow && (
        <motion.div
          {...REVEAL_EYEBROW}
          className="flex items-center gap-[2rem]">
          <div
            className="flex items-center gap-[0.375rem]"
            aria-hidden="true">
            <span className="h-[1.5rem] animate-chip-bar bg-[#f50]" />
            <span className="h-[1.5rem] animate-chip-bar bg-[#f50] [animation-delay:-1.333s]" />
            <span className="h-[1.5rem] animate-chip-bar bg-[#f50] [animation-delay:-0.667s]" />
          </div>
          <p className="m-0 font-spacemono text-[2rem] leading-[1.4] text-white uppercase">
            <ScrambleText
              text={eyebrow}
              trigger="mount"
              delay={150}
              duration={900}
            />
          </p>
        </motion.div>
      )}

      <h1 className="m-0 font-helveticaNeue text-[5rem] leading-[1.04] font-normal tracking-[-0.03em] text-white lg:text-[8rem]">
        {words.map((word, i) => (
          <React.Fragment key={`${word}-${i}`}>
            <motion.span
              {...REVEAL_WORD}
              transition={{ ...WORD_TRANSITION, delay: WORD_DELAY + i * WORD_STAGGER }}
              className="inline-block">
              {word}
            </motion.span>
            {i < words.length - 1 && " "}
          </React.Fragment>
        ))}
      </h1>

      {description && (
        <motion.p
          {...REVEAL_DESCRIPTION}
          transition={{ duration: 0.8, ease: [...EASE], delay: WORD_DELAY + words.length * WORD_STAGGER + 0.1 }}
          className="m-0 max-w-[86rem] font-helveticaNeue text-[2rem] leading-[1.5] text-white/70">
          {description}
        </motion.p>
      )}
    </header>
  );
}
